import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  AgentInterceptionType,
  messagingActions,
} from '../../reducers/messagingSlice';
import { selectIsAIAutoMode } from '../../redux/features/ai/ai.selector';
import { AgentMode } from '../../redux/features/agentMode/agentMode.constants';
import { selectIsAgentActionBlocked } from '../../redux/features/agentMode/agentMode.selector';
import { TaskStatuses } from '../../redux/features/taskRouter/taskRouter.constants';
import { selectCurrentTask } from '../../redux/features/taskRouter/taskRouter.selector';
import { updateTaskStatus } from '../../redux/features/taskRouter/taskRouter.slice';
import { selectAIIndicator } from '../../selectors/restaurant';
import logger from '../../utils/logger';

type UseAgentInterceptionOptions = {
  /**
   * Skip HITL-1606's mode gate. Set ONLY where the interception is not an agent
   * action — today just the task-router menu-load failure
   * (`useTaskRouterMenuHandlers`), which speaks to the customer and logs the
   * agent out because the menu could not be loaded. That is the system acting,
   * not the human, so the mode must not suppress it.
   */
  bypassModeGate?: boolean;
};

/**
 * HITL-1606 — this hook is the single gate in front of every agent action.
 *
 * Every agent action that matters routes through here: 14 of the 15
 * `sendAgentInterception` call sites, keyboard paths included (the intent
 * hotkeys fire the same handlers the buttons do). The 15th is the AI's own
 * `/upsell` TTS, which dispatches `messagingActions.sendAgentInterception`
 * DIRECTLY from `menuSlice` and so bypasses this hook — which is exactly why
 * the gate belongs here and not in the slice or the middleware: this hook is
 * the agent/AI boundary, by construction.
 *
 * Refusing here is sufficient for safety on its own. No interception means
 * `isAIAutoMode` is never cleared, which means every one of the ~20 behaviours
 * keyed off it — the POS submit guard, the hypothesis replay, the agent_cart
 * publish, the auto-resend — behaves exactly as it does in production today,
 * with the AI driving and a human watching. Not one of them is migrated onto
 * the mode. Disabling the controls (the rest of HITL-1606) is UX: it stops the
 * agent attempting what would be refused here. A control that slips through
 * costs a local edit the AI's next hypothesis frame overwrites, never an order.
 */
function useAgentInterception(options: UseAgentInterceptionOptions = {}) {
  const { bypassModeGate = false } = options;
  const dispatch = useAppDispatch();

  const currentTaskId = useAppSelector(selectCurrentTask);
  const isAIEnabled = useAppSelector(selectAIIndicator);
  const isAIAutoMode = useAppSelector(selectIsAIAutoMode);
  const isAgentActionBlocked = useAppSelector(selectIsAgentActionBlocked);

  const sendAgentInterception = (
    ...args: Parameters<typeof messagingActions.sendAgentInterception>
  ) => {
    // The gate. Warn rather than fail silently: every warning names a control
    // that should have been disabled and was not, which is how the UI pass gets
    // verified in the field.
    if (isAgentActionBlocked && !bypassModeGate) {
      logger.warn({
        message: `Agent interception refused: the agent is not in ${AgentMode.intercept} mode`,
        moreInfo: args?.[0]?.data?.type,
      });
      return;
    }

    //This event is like agent activity
    dispatch(messagingActions.sendAgentInterception(...args));

    //This event internally used by HITL to populate informatory messages
    const isTTSSentInterception =
      args?.[0]?.data?.type === AgentInterceptionType.TTS_SENT;
    if (currentTaskId && !isTTSSentInterception) {
      dispatch(
        updateTaskStatus({
          taskId: currentTaskId,
          event: TaskStatuses.agentInterception,
        })
      );
    }

    //This event to be used by POS to identify first agent interception
    if (isAIEnabled && isAIAutoMode) {
      dispatch(messagingActions.sendAgentFirstInterception(...args));
    }
  };

  return { sendAgentInterception };
}

export default useAgentInterception;
